import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { Button, Card, CardBody, Col, Container, Input, Row } from "reactstrap";
import Select from "react-select";
import TableContainer from "components/Common/TableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import Loaders from "components/Common/Loaders";
import { useLocation, useNavigate } from "react-router-dom";
import { DEFAULT_PAGE_SIZE, urlEndPoint } from "helpers/GeneralHelper";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";
import Swal from "sweetalert2";
import {
  submitUserSystemFilterData,
  fetchUserSystemTableAndFilterSchema,
  fetchCommonWorkShiftList,
  fetchCommonUserBlockStatusList,
  fetchCommonUserTypeList,
  mapUserBlockStatusApiToSelectOptions,
  resetUserSystemBlock,
} from "model/UserModel";
import { toast } from "react-toastify";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { usePermissionContext } from "libs/context/PermissionContext";

import ClientAccessAddEditModal from "pages/AllCommonModal/ClientAccessModal/client-access-add-edit-modal";
import TemporaryClientAccessAddEditModal from "pages/AllCommonModal/ClientAccessModal/temporary-client-access-add-edit-modal";
import ExtraPermissionsAddEditModal from "pages/AllCommonModal/ClientAccessModal/extra-permissions-add-edit-modal";
import "./manage-user.scss";

const unwrapArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  const payloadRoot = payload?.data ?? payload;
  if (Array.isArray(payloadRoot)) return payloadRoot;
  const nestedRecords =
    payloadRoot?.records ?? payloadRoot?.rows ?? payloadRoot?.items;
  return Array.isArray(nestedRecords) ? nestedRecords : [];
};

const normalizeId = (value) => String(value ?? "").trim();

const resolveBlockStatusFilterValue = (blockStatus) => {
  const normalized = String(blockStatus ?? "").trim().toLowerCase();
  if (normalized === "true") return true;
  if (normalized === "false") return false;
  return undefined;
};

const selectZIndexStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const MANAGE_USER_FILTER_CHIP_BG = "#0d1b6e";

const manageUserFilterMultiSelectStyles = {
  ...selectZIndexStyles,
  control: (base) => ({
    ...base,
    minHeight: 38,
    maxHeight: 38,
    overflow: "hidden",
  }),
  valueContainer: (base) => ({
    ...base,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    alignItems: "center",
    overflowX: "auto",
    overflowY: "hidden",
    padding: "2px 6px",
    gap: 4,
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  }),
  multiValue: (base) => ({
    ...base,
    flex: "0 0 auto",
    margin: 0,
    backgroundColor: MANAGE_USER_FILTER_CHIP_BG,
    borderRadius: 2,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#fff",
    fontSize: "0.8125rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    padding: "1px 4px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#fff",
    backgroundColor: "transparent",
    ":hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      color: "#fff",
    },
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    minWidth: 2,
    width: 2,
    flex: "0 0 auto",
  }),
  option: (base, { isSelected, isFocused }) => ({
    ...base,
    backgroundColor: isSelected
      ? MANAGE_USER_FILTER_CHIP_BG
      : isFocused
        ? "#e7f3ff"
        : base.backgroundColor,
    color: isSelected ? "#fff" : base.color,
  }),
};

const defaultTableMetaData = [
  { Header: "ID", accessor: "id" },
  { Header: "Name", accessor: "name" },
  { Header: "Email", accessor: "email" },
  { Header: "Type", accessor: "type" },
  { Header: "Reg Date", accessor: "regDate" },
  { Header: "Online", accessor: "online" },
  { Header: "Shift", accessor: "shift" },
  { Header: "Status", accessor: "status" },
];

const hasManageUserRowActions = (actions = {}) =>
  Boolean(
    actions.canEdit ||
      actions.canClientAccess ||
      actions.canTempClientAccess ||
      actions.canPermission ||
      actions.canResetBlock
  );

const EMPTY_MANAGE_USER_FILTERS = {
  userName: "",
  startDate: "",
  endDate: "",
  blockStatus: "",
  userTypeIds: [],
  workShiftIds: [],
};

const ManageUser = () => {
  const { fetchMetadataSchema } = useCommonFilterOptions();
  const { permissionData } = usePermissionContext() || {};
  const [filterForm, setFilterForm] = useState(() => ({ ...EMPTY_MANAGE_USER_FILTERS }));
  const [blockStatusOptions, setBlockStatusOptions] = useState([]);
  const [userTypeOptions, setUserTypeOptions] = useState([]);
  const [workShiftOptions, setWorkShiftOptions] = useState([]);

  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetaData] = useState([]);
  const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });
  const [clientAccessModalOpen, setClientAccessModalOpen] = useState(false);
  const [clientAccessUser, setClientAccessUser] = useState(null);
  const [tempClientAccessModalOpen, setTempClientAccessModalOpen] = useState(false);
  const [tempClientAccessUser, setTempClientAccessUser] = useState(null);
  const [extraPermissionsModalOpen, setExtraPermissionsModalOpen] = useState(false);
  const [extraPermissionsUser, setExtraPermissionsUser] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const companyId = new URLSearchParams(location?.search || "").get("companyId");
  const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("ManageUser");
  const initialFilterFetchedRef = useRef(false);

  const getSelectedOption = (options, selectedValue) =>
    options.find((option) => String(option?.value) === String(selectedValue)) ||
    null;

  const getSelectedOptions = (options, selectedValues = []) => {
    const values = Array.isArray(selectedValues) ? selectedValues : [];
    return options.filter((option) =>
      values.some((value) => String(value) === String(option?.value))
    );
  };

  const buildUserSystemFiltersFromSnapshot = (filterSnapshot) => {
    const filters = {};
    const nameSearch = String(filterSnapshot.userName || "").trim();
    if (nameSearch) {
      filters.nameContains = nameSearch;
      filters.search = nameSearch;
    }
    if (filterSnapshot.startDate) filters.fromDate = filterSnapshot.startDate;
    if (filterSnapshot.endDate) filters.toDate = filterSnapshot.endDate;

    const blockStatus = resolveBlockStatusFilterValue(filterSnapshot.blockStatus);
    if (blockStatus !== undefined) filters.blockStatus = blockStatus;

    const userTypeIds = (
      Array.isArray(filterSnapshot.userTypeIds) ? filterSnapshot.userTypeIds : []
    )
      .map(normalizeId)
      .filter(Boolean);
    if (userTypeIds.length) filters.userTypeIds = userTypeIds;

    const workShiftIds = (
      Array.isArray(filterSnapshot.workShiftIds) ? filterSnapshot.workShiftIds : []
    )
      .map(normalizeId)
      .filter(Boolean);
    if (workShiftIds.length) filters.workShiftIds = workShiftIds;

    return filters;
  };

  const buildUserSystemFiltersFromForm = useCallback(() => {
    return buildUserSystemFiltersFromSnapshot(filterForm);
  }, [filterForm]);

  const loadFilterDropdowns = useCallback(async () => {
    try {
      const [
        userTypesApiResponse,
        workShiftsApiResponse,
        blockStatusesApiResponse,
      ] = await Promise.all([
        fetchCommonUserTypeList(),
        fetchCommonWorkShiftList(),
        fetchCommonUserBlockStatusList(),
      ]);

      const userTypeRows = unwrapArray(
        userTypesApiResponse?.data ?? userTypesApiResponse
      );
      setUserTypeOptions(
        userTypeRows
          .map((userTypeRow) => ({
            value: normalizeId(userTypeRow?.id ?? userTypeRow?._id),
            label:
              userTypeRow?.display ??
              userTypeRow?.name ??
              userTypeRow?.title ??
              normalizeId(userTypeRow?.id),
          }))
          .filter((option) => option.value)
      );

      const workShiftRows = unwrapArray(
        workShiftsApiResponse?.data ?? workShiftsApiResponse
      );
      setWorkShiftOptions(
        workShiftRows
          .map((workShiftRow) => {
            const shiftId = normalizeId(workShiftRow?.id ?? workShiftRow?._id);
            const shiftLabel =
              workShiftRow?.display ??
              workShiftRow?.name ??
              workShiftRow?.title ??
              workShiftRow?.workShiftTitle ??
              workShiftRow?.workShiftTimeDisplay ??
              shiftId;
            return { value: shiftId, label: String(shiftLabel) };
          })
          .filter((option) => option.value)
      );

      setBlockStatusOptions(
        mapUserBlockStatusApiToSelectOptions(
          blockStatusesApiResponse?.data ?? blockStatusesApiResponse
        )
      );
    } catch (error) {
      toast.error(error?.message || "Unable to load filter options");
    }
  }, []);

  const getUserTableSchema = async () => {
    const metadataResponse = await fetchMetadataSchema(
      "metadata:userSystem",
      () => fetchUserSystemTableAndFilterSchema({})
    );
    const columns = metadataResponse?.data?.table?.columns || [];
    if (Array.isArray(columns) && columns.length) {
      setTableMetaData(
        columns.map((col) => ({
          Header: col?.title,
          accessor: col?.field,
          type: col?.type,
        }))
      );
      return;
    }
    setTableMetaData(defaultTableMetaData);
  };

  const filteredData = useMemo(() => userData, [userData]);

  const canCreateManageUser = Boolean(permissionData?.UserAddAccess);

  const hasAnyManageUserRowAction = useMemo(
    () => userData.some((row) => hasManageUserRowActions(row?.actions)),
    [userData]
  );

  const getUserList = async (payloadOverrides = {}, filterSnapshot = null) => {
    setLoading(true);
    const filterBody = filterSnapshot
      ? buildUserSystemFiltersFromSnapshot(filterSnapshot)
      : buildUserSystemFiltersFromForm();
    const requestPayloadForApi = {
      ...filterBody,
      pageNumber: payloadOverrides.pageNumber ?? requestPayload.pageNumber ?? 1,
      pageSize: payloadOverrides.pageSize ?? requestPayload.pageSize ?? DEFAULT_PAGE_SIZE,
      ...(companyId ? { companyId } : {}),
      ...payloadOverrides,
    };

    const filterResponse = await submitUserSystemFilterData(
      requestPayloadForApi
    );
    const paginationPayload = filterResponse?.data || {};
    const rawUserRows =
      paginationPayload?.records ??
      paginationPayload?.rows ??
      paginationPayload ??
      [];
    const normalizedUserRows = (
      Array.isArray(rawUserRows) ? rawUserRows : []
    ).map((userRow) => ({
      ...userRow,
      id: userRow?.id ?? userRow?._id ?? userRow?.userId ?? "",
      name: userRow?.name ?? userRow?.userName ?? "",
      email: userRow?.email ?? "",
      type: userRow?.type ?? userRow?.userType ?? "",
      regDate:
        userRow?.regDate ??
        userRow?.reg_date ??
        userRow?.createdOn ??
        "",
      online: userRow?.online ?? userRow?.isOnline ?? "",
      shift: userRow?.shift ?? userRow?.workShift ?? "",
      status: userRow?.status ?? "",
      blocked: Boolean(userRow?.blocked),
      actions: userRow?.actions ?? {},
    }));

    if (filterResponse) {
      setUserData(normalizedUserRows);
      setPaginationMeta({
        pageNumber:
          Number(paginationPayload?.pageNumber) ||
          Number(requestPayloadForApi?.pageNumber) ||
          1,
        pageSize:
          Number(paginationPayload?.pageSize) ||
          Number(requestPayloadForApi?.pageSize) ||
          100,
        totalPages: Number(paginationPayload?.totalPages) || 0,
        totalRecords: Number(paginationPayload?.totalRecords) || 0,
      });
      setRequestPayload(requestPayloadForApi);
    } else {
      setUserData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    getUserTableSchema();
    loadFilterDropdowns();
  }, [loadFilterDropdowns]);

  useEffect(() => {
    initialFilterFetchedRef.current = false;
  }, [companyId]);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    setFilterForm({ ...EMPTY_MANAGE_USER_FILTERS });
    getUserList({ pageNumber: 1, pageSize }, { ...EMPTY_MANAGE_USER_FILTERS });
  }, [settingsReady, settingsPageSize, companyId]);

  const handleFilterFieldChange = (event) => {
    const { name, value } = event.target;
    setFilterForm((previousFilters) => ({ ...previousFilters, [name]: value }));
  };

  const handleFilterSelectChange = (fieldName, selectedOption) => {
    setFilterForm((previousFilters) => ({
      ...previousFilters,
      [fieldName]: selectedOption?.value ?? "",
    }));
  };

  const handleFilterMultiSelectChange = (fieldName, selectedOptions) => {
    const values = Array.isArray(selectedOptions)
      ? selectedOptions.map((option) => normalizeId(option?.value)).filter(Boolean)
      : [];
    setFilterForm((previousFilters) => ({
      ...previousFilters,
      [fieldName]: values,
    }));
  };

  const handleSearchClick = async () => {
    await getUserList({ pageNumber: 1 });
  };

  const resolveRowId = (row) =>
    String(row?.id ?? row?._id ?? row?.userId ?? "").trim();

  const handleEdit = (row) => {
    if (!row?.actions?.canEdit) return;
    const id = resolveRowId(row);
    if (!id) {
      toast.error("User id is missing");
      return;
    }
    navigate(`/${urlEndPoint.manageUserAddEdit}/${encodeURIComponent(id)}`);
  };

  const handleClientAccess = (row) => {
    if (!row?.actions?.canClientAccess) return;
    const id = resolveRowId(row);
    if (!id) {
      toast.error("User id is missing");
      return;
    }
    setClientAccessUser(row);
    setClientAccessModalOpen(true);
  };

  const handleTemporaryClientAccess = (row) => {
    if (!row?.actions?.canTempClientAccess) return;
    const id = resolveRowId(row);
    if (!id) {
      toast.error("User id is missing");
      return;
    }
    setTempClientAccessUser(row);
    setTempClientAccessModalOpen(true);
  };

  const handlePermission = (row) => {
    if (!row?.actions?.canPermission) return;
    const id = resolveRowId(row);
    if (!id) {
      toast.error("User id is missing");
      return;
    }
    setExtraPermissionsUser(row);
    setExtraPermissionsModalOpen(true);
  };

  const handleResetBlock = async (row) => {
    if (!row?.actions?.canResetBlock || !row?.blocked) return;
    const id = resolveRowId(row);
    if (!id) {
      toast.error("User id is missing");
      return;
    }
    const swalResult = await Swal.fire({
      title: "Reset block?",
      text: "Are you sure you want to reset the block for this user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!swalResult?.isConfirmed) return;

    const response = await resetUserSystemBlock(id);
    if (response?.success || response?.isSuccess || response?.status) {
      toast.success(response?.message || "Block reset successfully");
      await getUserList({ pageNumber: paginationMeta?.pageNumber || 1 });
      return;
    }
    toast.error(response?.message || "Unable to reset block");
  };

  const handlePageChange = async (page) => {
    await getUserList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getUserList({ pageNumber: 1, pageSize: rows });
  };

  return (
    <React.Fragment>
      <div className="page-content manage-user-page">
        <Container fluid>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-3">
                  Manage User
                  {companyId ? ` (Company ID: ${companyId})` : ""}
                </h5>
                {canCreateManageUser ? (
                  <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                    <Button
                      color="primary"
                      onClick={() => navigate(`/${urlEndPoint.manageUserAddEdit}`)}
                    >
                      + Create
                    </Button>
                  </Col>
                ) : null}
              </div>
              <hr />

              <LegacyFilterToolbar className="mb-4">
                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                  <Col xs={12} md={2} className="metadata-filter-field metadata-filter-field--text">
                    <span className="manage-user-filter-label">User Name</span>
                    <div className="metadata-filter-field__control">
                    <Input
                      type="text"
                      name="userName"
                      placeholder="User Name"
                      className={LEGACY_FILTER_INPUT_CLASS}
                      value={filterForm.userName}
                      onChange={(e) => {
                        const nextValue = e?.target?.value ?? "";
                        setFilterForm((previousFilters) => ({
                          ...previousFilters,
                          userName: nextValue,
                        }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchClick();
                        }
                      }}
                    />
                    </div>
                  </Col>
                  <Col xs={12} md={2} className="metadata-filter-field metadata-filter-field--date">
                    <span className="manage-user-filter-label">Start date</span>
                    <div className="metadata-filter-field__control">
                    <Input
                      type="date"
                      name="startDate"
                      className={LEGACY_FILTER_INPUT_CLASS}
                      value={filterForm.startDate}
                      onChange={handleFilterFieldChange}
                    />
                    </div>
                  </Col>
                  <Col xs={12} md={2} className="metadata-filter-field metadata-filter-field--date">
                    <span className="manage-user-filter-label">End date</span>
                    <div className="metadata-filter-field__control">
                    <Input
                      type="date"
                      name="endDate"
                      className={LEGACY_FILTER_INPUT_CLASS}
                      value={filterForm.endDate}
                      onChange={handleFilterFieldChange}
                    />
                    </div>
                  </Col>
                  <Col xs={12} md={2} className="metadata-filter-field metadata-filter-field--dropdown">
                    <span className="manage-user-filter-label">Block Status</span>
                    <div className="metadata-filter-field__control">
                    <Select
                      options={blockStatusOptions}
                      className="select2-selection"
                      placeholder="Block Status"
                      isClearable
                      value={getSelectedOption(blockStatusOptions, filterForm.blockStatus)}
                      onChange={(opt) => handleFilterSelectChange("blockStatus", opt)}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      styles={selectZIndexStyles}
                    />
                    </div>
                  </Col>
                  <Col xs={12} md={2} className="metadata-filter-field metadata-filter-field--dropdown">
                    <span className="manage-user-filter-label">Select Type</span>
                    <div className="metadata-filter-field__control">
                    <Select
                      options={userTypeOptions}
                      className="select2-selection"
                      classNamePrefix="manage-user-filter-multiselect"
                      placeholder="Select Type"
                      isMulti
                      isClearable
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      value={getSelectedOptions(userTypeOptions, filterForm.userTypeIds)}
                      onChange={(opts) => handleFilterMultiSelectChange("userTypeIds", opts)}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      styles={manageUserFilterMultiSelectStyles}
                    />
                    </div>
                  </Col>
                  <Col xs={12} md={2} className="metadata-filter-field metadata-filter-field--dropdown">
                    <span className="manage-user-filter-label">Select Shift</span>
                    <div className="metadata-filter-field__control">
                    <Select
                      options={workShiftOptions}
                      className="select2-selection"
                      classNamePrefix="manage-user-filter-multiselect"
                      placeholder="Select Shift"
                      isMulti
                      isClearable
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      value={getSelectedOptions(workShiftOptions, filterForm.workShiftIds)}
                      onChange={(opts) => handleFilterMultiSelectChange("workShiftIds", opts)}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      styles={manageUserFilterMultiSelectStyles}
                    />
                    </div>
                  </Col>
                </Row>
                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end justify-content-end">
                <Col xs={12} md={2} className="metadata-filter-field metadata-filter-field--actions">
                    <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                    <div className="metadata-filter-toolbar__actions">
                    <Button
                      color="primary"
                      type="button"
                      className="manage-user-search-btn w-100"
                      onClick={handleSearchClick}
                    >
                      <i className="fa fa-search me-1" aria-hidden="true" />
                      Search
                    </Button>
                    </div>
                  </Col>
                </Row>
              </LegacyFilterToolbar>

              {!loading && tableMetaData?.length ? (
                <TableContainer
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyManageUserRowAction}
                  onEdit={handleEdit}
                  onClientAccess={handleClientAccess}
                  onTemporaryClientAccess={handleTemporaryClientAccess}
                  onPermission={handlePermission}
                  onResetBlock={handleResetBlock}
                  isCheckboxLeftSide={false}
                  isPagination
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  pageName="ManageUser"
                />
              ) : (
                <Loaders />
              )}
            </CardBody>
          </Card>
        </Container>

        <ClientAccessAddEditModal
          clientAccessModalOpen={clientAccessModalOpen}
          setClientAccessModalOpen={setClientAccessModalOpen}
          userRow={clientAccessUser}
        />
        <TemporaryClientAccessAddEditModal
          tempClientAccessModalOpen={tempClientAccessModalOpen}
          setTempClientAccessModalOpen={setTempClientAccessModalOpen}
          userRow={tempClientAccessUser}
        />
        <ExtraPermissionsAddEditModal
          extraPermissionsModalOpen={extraPermissionsModalOpen}
          setExtraPermissionsModalOpen={setExtraPermissionsModalOpen}
          userRow={extraPermissionsUser}
        />
      </div>
    </React.Fragment>
  );
};

ManageUser.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(ManageUser);
